import { useEffect, useRef, useState } from "react";
import qapi from "views_shared/app/javascript/api";
import qlog from "views_shared/app/javascript/scripts/qlog";

/**
 * useMediaPosts — data half of the media profile's Posts card (the chrome half is
 * media_requests_section). Reads one page of the reporter's requests from the paged
 * get_requests_for_media_profile RPC; every row carries `total_count`, the number of requests
 * matching the search, which sizes the pager.
 *
 * Only the latest read may commit: a page click or keystroke that lands before the previous read
 * resolves would otherwise let the slower, older page overwrite the newer one. A failed read
 * settles empty rather than surfacing to the page — this is display content, not a form.
 *
 * Each result remembers the inputs it was read for, and `isLoading` is true whenever they differ from
 * the current ones — already in the render that changes them, so callers never mistake the previous
 * read's rows (say, a search that matched nothing) for the new one's.
 *
 * @param {Object} options
 * @param {number|string} options.mediaProfileId - The media profile whose posts to read
 * @param {number} options.page - 1-based page
 * @param {number} options.perPage - Rows per page
 * @param {"newest"|"oldest"} options.sort - Posting order
 * @param {string} options.search - Matches the title or details ("" for none)
 * @returns {{ posts: Array, total: number, isLoading: boolean, hasLoaded: boolean }}
 */
const readKey = (mediaProfileId, page, perPage, sort, search) =>
  JSON.stringify([mediaProfileId, page, perPage, sort, search]);

function useMediaPosts({ mediaProfileId, page, perPage, sort, search }) {
  const [result, setResult] = useState({ posts: [], total: 0, hasLoaded: false, key: null });
  const latestRead = useRef(0);

  useEffect(() => {
    if (!mediaProfileId) {
      return;
    }

    latestRead.current += 1;
    const read = latestRead.current;
    const key = readKey(mediaProfileId, page, perPage, sort, search);
    const settle = (posts) => {
      if (read !== latestRead.current) {
        return;
      }
      setResult({
        posts,
        total: posts.length ? Number(posts[0].total_count) || posts.length : 0,
        hasLoaded: true,
        key,
      });
    };

    qapi.requests
      .getForMediaProfile(mediaProfileId, { limit: perPage, offset: (page - 1) * perPage, sort, search })
      .then((response) => {
        if (response?.error) {
          qlog.error("Posts lookup failed:", response.error);
        }
        settle(Array.isArray(response?.data) ? response.data : []);
      })
      .catch((error) => {
        qlog.error("Posts lookup failed:", error);
        settle([]);
      });
  }, [mediaProfileId, page, perPage, sort, search]);

  const { posts, total, hasLoaded, key } = result;
  return { posts, total, hasLoaded, isLoading: key !== readKey(mediaProfileId, page, perPage, sort, search) };
}

export default useMediaPosts;
